import React, { useEffect,useRef, useState } from "react";
import { Card, CardHeader, CardTitle, CardContent } from "../ui/card";
import { 
  Package2, Box, Image, Loader2, ArrowLeft, ChartBarIcon,
  Boxes, X, ZoomIn, Calendar, Hash, ArrowRight, LineChart as LineChartIcon,
  ZoomOut, ChevronLeft, ChevronRight, BarChart as BarChartIcon,
  PieChart as PieChartIcon
} from "lucide-react";
import { Alert, AlertDescription } from "../ui/alert";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  ResponsiveContainer, PieChart, Pie, Cell, LineChart, Line
} from 'recharts';

const renderCustomizedLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, percent, name, color }) => {
  const RADIAN = Math.PI / 180;
  const radius = outerRadius * 1.2;
  const x = cx + radius * Math.cos(-midAngle * RADIAN);
  const y = cy + radius * Math.sin(-midAngle * RADIAN);
  const anchor = x > cx ? 'start' : 'end';

  return (
    <text
      x={x}
      y={y}
      fill={color}
      textAnchor={anchor}
      dominantBaseline="central"
      className="text-sm font-medium"
    >
      {`${name} (${(percent * 100).toFixed(0)}%)`}
    </text>
  );
};

const ViewChoiceCard = ({ title, description, icon: Icon, onClick }) => (
  <Card 
    onClick={onClick}
    className="bg-gray-800 border-gray-700 hover:border-blue-500 cursor-pointer transform hover:scale-[1.02] transition-all duration-200 overflow-hidden group relative"
  >
    <CardContent className="p-8">
      <div className="flex flex-col items-center space-y-6">
        <div className="w-20 h-20 rounded-full bg-gradient-to-br from-blue-500/20 to-purple-500/20 flex items-center justify-center group-hover:from-blue-500/30 group-hover:to-purple-500/30 transition-all duration-300">
          <Icon className="w-10 h-10 text-blue-400 group-hover:text-blue-300 transition-colors" />
        </div>
        <div className="text-center">
          <h3 className="text-2xl font-bold text-white mb-3">{title}</h3>
          <p className="text-gray-400">{description}</p>
        </div>
        <div className="flex items-center text-blue-400 group-hover:text-blue-300">
          <span className="mr-2">Explore</span>
          <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
        </div>
      </div>
    </CardContent>
    <div className="absolute inset-0 bg-gradient-to-br from-blue-500/5 to-purple-500/5 group-hover:from-blue-500/10 group-hover:to-purple-500/10 transition-all duration-300" />
  </Card>
);
const COLORS = ['#3B82F6', '#8B5CF6', '#EC4899', '#10B981', '#F59E0B', '#6366F1'];
  
const renderCustomLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, percent, index, name }) => {
  const RADIAN = Math.PI / 180;
  // Increase the radius to move labels further from the pie
  const radius = outerRadius * 1.4;
  
  // Calculate base position
  const x = cx + radius * Math.cos(-midAngle * RADIAN);
  const y = cy + radius * Math.sin(-midAngle * RADIAN);

  // Add vertical offset based on index
  const verticalOffset = index * 4;
  const finalY = y + (y > cy ? verticalOffset : -verticalOffset);

  return (
    <text
      x={x}
      y={finalY}
      fill={COLORS[index % COLORS.length]}
      textAnchor={x > cx ? 'start' : 'end'}
      dominantBaseline="central"
      fontSize="12"
    >
      {`${name} (${(percent * 100).toFixed(0)}%)`}
    </text>
  );
};

const AnalyticsView = ({ defectData, product, size }) => {
  // const COLORS = ['#3B82F6', '#8B5CF6', '#EC4899', '#10B981', '#F59E0B', '#6366F1'];
  
  const processDataForCharts = () => {
    const defects = defectData[product]?.[size] || [];
    return defects.map(defect => ({
      name: defect.defect,
      count: defect.count
    }));
  };



  const CustomLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, percent, index, name, payload }) => {
    const RADIAN = Math.PI / 180;
    // Increase radius for label placement
    const radius = outerRadius * 1.05;
    
    // Calculate the position on the larger circle
    const x = cx + radius * Math.cos(-midAngle * RADIAN);
    const y = cy + radius * Math.sin(-midAngle * RADIAN);
    
    // Calculate line points
    const innerX = cx + (outerRadius + 5) * Math.cos(-midAngle * RADIAN);
    const innerY = cy + (outerRadius + 5) * Math.sin(-midAngle * RADIAN);
    
    // Calculate if label should be aligned left or right of line
    const textAnchor = x > cx ? 'start' : 'end';
    
    // Format percentage
    const percentValue = (percent * 100).toFixed(0);
    
    // Don't render tiny slices' labels to prevent clutter
    if (percentValue < 2) return null;
  
    return (
      <g>
        {/* Line from slice to label */}
        <path
          d={`M ${innerX},${innerY} L ${x},${y}`}
          stroke={COLORS[index % COLORS.length]}
          strokeWidth={0.5}
          fill="none"
        />
        
        {/* Label background for better readability */}
        <rect
          x={textAnchor === 'start' ? x : x - 100}
          y={y - 8}
          width={100}
          height={16}
          fill="rgba(17, 24, 39, 0.8)"
          rx={4}
        />
        
        {/* Label text */}
        <text
          x={x}
          y={y}
          fill={COLORS[index % COLORS.length]}
          textAnchor={textAnchor}
          dominantBaseline="central"
          fontSize={12}
          fontWeight="500"
        >
          {`${name} (${percentValue}%)`}
        </text>
      </g>
    );
  };
  
  
  const chartData = processDataForCharts();


  const [windowWidth, setWindowWidth] = useState(typeof window !== 'undefined' ? window.innerWidth : 0);

  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Calculate dynamic dimensions
  const chartHeight = windowWidth < 768 ? 400 : 500; // Increased height for better label spacing
  const outerRadius = windowWidth < 768 ? 80 : 120;
  const innerRadius = windowWidth < 768 ? 30 : 40; // Added inner radius for donut style

  if (chartData.length === 0) {
    return (
      <CenteredAlert
        icon={ChartBarIcon}
        title="No Analytics Data Available"
        message="There is currently no defect data available for analytics. Please select a different product or size combination."
        className="border-blue-500/50"
      />
    );
  };
  return (
    <div className="space-y-8">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Bar Chart Card */}
        <Card className="bg-gray-800 border-gray-700 p-6">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <BarChartIcon className="w-5 h-5 text-blue-400" />
              Defect Distribution
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-[300px] w-full">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={chartData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                  <XAxis dataKey="name" stroke="#9CA3AF" />
                  <YAxis stroke="#9CA3AF" />
                  <Tooltip contentStyle={{ backgroundColor: '#1F2937', border: 'none', borderRadius: '8px' }} labelStyle={{ color: '#F3F4F6' }} />
                  <Bar dataKey="count" fill="#3B82F6" radius={[4, 4, 0, 0]}>
                    {chartData.map((entry, index) => (
                      <Cell key={index} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gray-800 border-gray-700 p-4 md:p-6">
      <CardHeader className="p-4">
        <CardTitle className="text-white flex items-center gap-2 text-base md:text-lg">
          <PieChartIcon className="w-5 h-5 text-purple-400" />
          Defect Proportion
        </CardTitle>
      </CardHeader>
      <CardContent className="p-0 md:p-4">
        <div className="h-[400px] md:h-[500px] w-full">
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie
                data={chartData}
                cx="50%"
                cy="50%"
                labelLine={false}
                label={CustomLabel}
                outerRadius={outerRadius}
                innerRadius={innerRadius}
                padAngle={1}
                fill="#8884d8"
                dataKey="count"
              >
                {chartData.map((entry, index) => (
                  <Cell 
                    key={`cell-${index}`} 
                    fill={COLORS[index % COLORS.length]}
                    stroke="rgba(17, 24, 39, 0.8)"
                    strokeWidth={1}
                  />
                ))}
              </Pie>
              <Tooltip 
                contentStyle={{ 
                  backgroundColor: '#1F2937', 
                  border: 'none', 
                  borderRadius: '8px',
                  fontSize: windowWidth < 768 ? '12px' : '14px'
                }} 
                labelStyle={{ color: '#F3F4F6' }} 
              />
            </PieChart>
          </ResponsiveContainer>
        </div>
      </CardContent>
    </Card>
      </div>

      <Card className="bg-gray-800 border-gray-700">
        <CardHeader>
          <CardTitle className="text-white">Summary Statistics</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="bg-gray-700/50 rounded-lg p-4">
              <div className="text-sm text-gray-400 mb-1">Total Defects</div>
              <div className="text-2xl font-bold text-white">
                {chartData.reduce((sum, item) => sum + item.count, 0)}
              </div>
            </div>
            <div className="bg-gray-700/50 rounded-lg p-4">
              <div className="text-sm text-gray-400 mb-1">Most Common Defect</div>
              <div className="text-2xl font-bold text-white">
                {chartData.reduce((max, item) => item.count > max.count ? item : max, { count: 0 }).name}
              </div>
            </div>
            <div className="bg-gray-700/50 rounded-lg p-4">
              <div className="text-sm text-gray-400 mb-1">Defect Types</div>
              <div className="text-2xl font-bold text-white">
                {chartData.length}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
const ImageViewer = ({ image, images, onClose, currentIndex, setCurrentIndex }) => {
  const [zoomLevel, setZoomLevel] = useState(1);
  const containerRef = useRef(null);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'ArrowLeft' && currentIndex > 0) {
        setCurrentIndex(currentIndex - 1);
      }
      if (e.key === 'ArrowRight' && currentIndex < images.length - 1) {
        setCurrentIndex(currentIndex + 1);
      }
      if (e.key === 'Escape') onClose();
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [currentIndex, images.length, onClose, setCurrentIndex]);

  // Handle click outside
  const handleContainerClick = (e) => {
    // Check if the click is on the container itself, not its children
    if (e.target === containerRef.current) {
      onClose();
    }
  };

  if (!image) return null;

  const handleZoom = (delta) => {
    setZoomLevel(prev => Math.max(0.5, Math.min(3, prev + delta)));
  };

  const navigateImage = (direction) => {
    const newIndex = currentIndex + direction;
    if (newIndex >= 0 && newIndex < images.length) {
      setCurrentIndex(newIndex);
    }
  };

  return (
    <div 
      ref={containerRef}
      className="fixed inset-0 z-50 bg-black/95 backdrop-blur-sm flex items-center justify-center animate-in fade-in duration-200"
      onClick={handleContainerClick}
    >
      {/* Navigation Arrows - Made larger and more visible */}
      {currentIndex > 0 && (
        <button
          onClick={(e) => {
            e.stopPropagation();
            navigateImage(-1);
          }}
          className="absolute left-4 top-1/2 -translate-y-1/2 p-4 bg-black/50 rounded-full hover:bg-black/70 transition-all hover:scale-110 focus:outline-none focus:ring-2 focus:ring-blue-500 group"
          aria-label="Previous image"
        >
          <ChevronLeft className="h-8 w-8 text-white group-hover:text-blue-400 transition-colors" />
        </button>
      )}

      {currentIndex < images.length - 1 && (
        <button
          onClick={(e) => {
            e.stopPropagation();
            navigateImage(1);
          }}
          className="absolute right-4 top-1/2 -translate-y-1/2 p-4 bg-black/50 rounded-full hover:bg-black/70 transition-all hover:scale-110 focus:outline-none focus:ring-2 focus:ring-blue-500 group"
          aria-label="Next image"
        >
          <ChevronRight className="h-8 w-8 text-white group-hover:text-blue-400 transition-colors" />
        </button>
      )}

      {/* Top Controls - Enhanced visibility and feedback */}
      <div className="absolute top-4 right-4 flex items-center gap-3 z-50">
        <button
          onClick={(e) => {
            e.stopPropagation();
            handleZoom(-0.2);
          }}
          className="p-3 bg-black/50 hover:bg-black/70 rounded-full transition-all hover:scale-110 focus:outline-none focus:ring-2 focus:ring-blue-500 group"
          aria-label="Zoom out"
        >
          <ZoomOut className="h-6 w-6 text-white group-hover:text-blue-400 transition-colors" />
        </button>
        <button
          onClick={(e) => {
            e.stopPropagation();
            handleZoom(0.2);
          }}
          className="p-3 bg-black/50 hover:bg-black/70 rounded-full transition-all hover:scale-110 focus:outline-none focus:ring-2 focus:ring-blue-500 group"
          aria-label="Zoom in"
        >
          <ZoomIn className="h-6 w-6 text-white group-hover:text-blue-400 transition-colors" />
        </button>
        <button
          onClick={(e) => {
            e.stopPropagation();
            onClose();
          }}
          className="p-3 bg-black/50 hover:bg-black/70 rounded-full transition-all hover:scale-110 hover:bg-red-900/50 focus:outline-none focus:ring-2 focus:ring-red-500 group"
          aria-label="Close viewer"
        >
          <X className="h-6 w-6 text-white group-hover:text-red-400 transition-colors" />
        </button>
      </div>

      {/* Main Image Container */}
      <div 
        className="relative max-w-[90vw] max-h-[90vh] flex items-center justify-center"
        onClick={(e) => e.stopPropagation()}
      >
        <img
          src={images[currentIndex].url}
          alt={`Defect ${currentIndex + 1}`}
          className="max-h-full max-w-full object-contain rounded-lg shadow-2xl transition-transform duration-200"
          style={{ transform: `scale(${zoomLevel})` }}
        />
      </div>

      {/* Enhanced Image Counter */}
      <div 
        className="absolute bottom-4 left-1/2 -translate-x-1/2 px-6 py-2 bg-black/50 rounded-full text-white font-medium backdrop-blur-sm text-sm transition-opacity"
        onClick={(e) => e.stopPropagation()}
      >
        {currentIndex + 1} of {images.length}
      </div>
    </div>
  );
};

const DetailItem = ({ icon, label, value }) => (
  <div className="flex items-start gap-3">
    <div className="p-2 bg-blue-500/10 rounded-lg">
      {icon}
    </div>
    <div>
      <p className="text-sm text-gray-400">{label}</p>
      <p className="text-white font-medium capitalize">{value}</p>
    </div>
  </div>
);

const ProductCard = ({ product, onSelect }) => (
  <Card 
    onClick={() => onSelect(product)}
    className="bg-gray-800 border-gray-700 hover:border-blue-500 cursor-pointer transform hover:scale-[1.02] transition-all duration-200"
  >
    <CardContent className="p-8">
      <div className="flex flex-col items-center space-y-4">
        <div className="w-20 h-20 rounded-full bg-blue-500/10 flex items-center justify-center">
          <Package2 className="w-10 h-10 text-blue-500" />
        </div>
        <div className="text-center">
          <h3 className="text-2xl font-bold text-white mb-2">
            {product === "product1" ? "Compound DS 3069 N Grey" : "Compound OP 649 LV Orange"}
          </h3>
          <p className="text-gray-400">Click to view defect analysis</p>
        </div>
        <div className="flex items-center text-blue-500 group">
          <span className="mr-2">View Details</span>
          <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
        </div>
      </div>
    </CardContent>
  </Card>
);
const NoDataAvailableAlert = ({ size, product }) => (
  <Alert className="bg-gray-800 border-yellow-500/20 max-w-2xl mx-auto">
    <div className="flex items-start gap-4">
      <div className="p-3 bg-yellow-500/10 rounded-xl">
        <Box className="h-6 w-6 text-yellow-500" />
      </div>
      <div>
        <h4 className="text-lg font-semibold text-white mb-2">No Data Available</h4>
        <AlertDescription className="text-gray-400">
          There is currently no defect data available for {size} size in {
            product === "product1" ? "Compound DS 3069 N Grey" : "Compound OP 649 LV Orange"
          }. Please select a different size or product.
        </AlertDescription>
      </div>
    </div>
  </Alert>
);

const SizeCard = ({ size, onSelect, disabled }) => {
  // Map the internal size values to display values
  const sizeDisplay = size === "small" ? "53 mm" : "57 mm";
  
  return (
    <Card 
      onClick={() => !disabled && onSelect(size)}
      className={`bg-gray-800 border-gray-700 transition-all duration-200 ${
        disabled 
          ? 'opacity-50 cursor-not-allowed' 
          : 'hover:border-blue-500 cursor-pointer transform hover:scale-[1.02]'
      }`}
    >
      <CardContent className="p-6">
        <div className="flex flex-col items-center space-y-4">
          <div className="w-16 h-16 rounded-full bg-blue-500/10 flex items-center justify-center">
            <Boxes className="w-8 h-8 text-blue-500" />
          </div>
          <div className="text-center">
            <h3 className="text-xl font-bold text-white">{sizeDisplay} Size</h3>
            {disabled && (
              <p className="text-sm text-red-400 mt-2">No data available</p>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};
const DefectCard = ({ defect, onSelect }) => (
  <Card 
    onClick={() => onSelect(defect)}
    className="bg-gray-800 border-gray-700 hover:border-blue-500 cursor-pointer group transition-all duration-200"
  >
    <CardContent className="p-6">
      <div className="flex flex-col items-center text-center space-y-4">
        <div className="w-16 h-16 rounded-full bg-blue-500/10 flex items-center justify-center group-hover:bg-blue-500/20 transition-colors">
          <Image className="h-8 w-8 text-blue-500" />
        </div>
        <div>
          <h3 className="text-lg font-bold text-white mb-2">{defect.defect}</h3>
          <p className="text-sm text-gray-400">
            {defect.count} {defect.count === 1 ? 'instance' : 'instances'} detected
          </p>
        </div>
      </div>
    </CardContent>
  </Card>
);

const CenteredAlert = ({ icon: Icon, title, message, className = "" }) => (
  <div className="flex items-center justify-center h-[60vh]">
    <Alert className={`bg-gray-800 border-gray-700 max-w-2xl mx-auto ${className}`}>
      <div className="flex items-start gap-4">
        <div className="p-3 bg-gray-700/50 rounded-xl">
          <Icon className="h-6 w-6 text-blue-500" />
        </div>
        <div>
          <h4 className="text-lg font-semibold text-white mb-2">{title}</h4>
          <AlertDescription className="text-gray-400">
            {message}
          </AlertDescription>
        </div>
      </div>
    </Alert>
  </div>
);

const NoDataAlert = () => (
  <CenteredAlert
    icon={Box}
    title="No Defect Classes Available"
    message="There are currently no defect classes available for this product and size combination. Please try selecting a different product or size."
    className="border-blue-500/50"
  />
);

const NoImagesAlert = () => (
  <CenteredAlert
    icon={Image}
    title="No Images Available"
    message="There are currently no images available for this defect type. Please try selecting a different defect class."
    className="border-yellow-500/50"
  />
);


const NavigationHeader = ({ title, subtitle, onBack }) => {
  // Function to replace product names in subtitle
  const getFormattedSubtitle = (subtitle) => {
    if (!subtitle) return subtitle;
    return subtitle
      .replace('product1', 'Compound DS 3069 N Grey')
      .replace('product2', 'Compound OP 649 LV Orange');
  };

  return (
    <div className="flex items-center justify-between mb-8">
      <div>
        <h2 className="text-2xl font-bold text-white">{title}</h2>
        {subtitle && <p className="text-gray-400 mt-1">{getFormattedSubtitle(subtitle)}</p>}
      </div>
      {onBack && (
        <button 
          onClick={onBack}
          className="flex items-center gap-2 text-gray-400 hover:text-white transition-colors"
        >
          <ArrowLeft className="h-5 w-5" />
          <span>Back</span>
        </button>
      )}
    </div>
  );
};
const DefectView = () => {
  const [defectClasses, setDefectClasses] = useState({});
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [selectedSize, setSelectedSize] = useState(null);
  const [selectedView, setSelectedView] = useState(null);
  const [selectedDefect, setSelectedDefect] = useState(null);
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const hasDataForSize = (product, size) => {
    return defectClasses[product]?.[size]?.length > 0;
  };

  const sizeMap = {
    'small': '53 mm',
    'large': '57 mm'
  };
  
  useEffect(() => {
    fetchDefectClasses();
  }, []);

  const fetchDefectClasses = async () => {
    try {
      const response = await fetch(
        "https://eyonyjgai6.execute-api.us-east-1.amazonaws.com/product-defects"
      );
      const data = await response.json();
      setDefectClasses(data);
    } catch (error) {
      console.error("Error fetching defect classes:", error);
    }
  };
  const renderSizeSelection = () => {
    return (
      <>
        <NavigationHeader 
          title="Select Size" 
          subtitle={`Viewing ${selectedProduct}`}
          onBack={() => setSelectedProduct(null)} 
        />
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 max-w-4xl mx-auto">
          {["small", "large"].map((size) => (
            <SizeCard 
              key={size} 
              size={size} 
              onSelect={(selectedSize) => {
                if (hasDataForSize(selectedProduct, selectedSize)) {
                  setSelectedSize(selectedSize);
                }
              }}
              disabled={!hasDataForSize(selectedProduct, size)}
            />
          ))}
        </div>
        {selectedProduct && (
          <div className="mt-6">
            <NoDataAvailableAlert 
              size="large" 
              product={selectedProduct} 
            />
          </div>
        )}
      </>
    );
  };


  const fetchImages = async (defect) => {
    setLoading(true);
    try {
      const formattedDefect = defect.defect.replace(/\s+/g, "_");
      const url = `https://eyonyjgai6.execute-api.us-east-1.amazonaws.com/products/${selectedProduct}/sizes/${selectedSize}/defect/${formattedDefect}`;
      
      const response = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
      });
  
      const data = await response.json();
  
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
  
      setImages(data.images || []);
      setSelectedDefect(defect);
    } catch (error) {
      console.error("Error fetching images:", error);
      setImages([]);
    } finally {
      setLoading(false);
    }
  };

  const renderImageGrid = () => (
    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3 sm:gap-4">
      {images.map((image, index) => (
        <div 
          key={index}
          onClick={() => {
            setSelectedImage(image);
            setCurrentImageIndex(index);
          }}
          className="group relative aspect-square rounded-xl overflow-hidden cursor-pointer bg-gray-800 hover:ring-2 hover:ring-blue-500 transition-all"
        >
          <img
            src={image.url}
            alt={`Defect ${index + 1}`}
            className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/40 to-transparent opacity-0 group-hover:opacity-100 transition-opacity">
            <div className="absolute bottom-0 left-0 right-0 p-3">
              <p className="text-white text-sm font-medium truncate">{image.defect}</p>
              <p className="text-gray-300 text-xs">Date: {image.date}</p>
            </div>
            <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
              <div className="p-2 bg-white/20 rounded-full backdrop-blur-sm">
                <ZoomIn className="h-5 w-5 text-white" />
              </div>
            </div>
          </div>
        </div>
      ))}
    </div>
  );

  const renderContent = () => {
    if (loading) {
      return (
        <div className="flex items-center justify-center h-[60vh]">
          <div className="text-center space-y-4">
            <Loader2 className="h-12 w-12 animate-spin text-blue-500 mx-auto" />
            <p className="text-gray-400">Loading content...</p>
          </div>
        </div>
      );
    }

    if (!selectedProduct) {
      return (
        <>
          <NavigationHeader title="Select Product" />
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-4xl mx-auto">
            {["product1", "product2"].map((product) => (
              <ProductCard 
                key={product} 
                product={product} 
                onSelect={setSelectedProduct} 
              />
            ))}
          </div>
        </>
      );
    }

    if (!selectedSize) {
      return (
        <>
          <NavigationHeader 
            title="Select Size" 
            subtitle={`Viewing ${selectedProduct}`}
            onBack={() => setSelectedProduct(null)} 
          />
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 max-w-4xl mx-auto">
            {["small", "large"].map((size) => (
              <SizeCard 
                key={size} 
                size={size} 
                onSelect={setSelectedSize} 
              />
            ))}
          </div>
        </>
      );
    }

    if (!selectedView) {
      return (
        <>
          <NavigationHeader 
            title="Select View"
            subtitle={`${selectedProduct} - ${selectedSize} size`}
            onBack={() => setSelectedSize(null)}
          />
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-4xl mx-auto">
            <ViewChoiceCard
              title="View Images"
              description="Browse and analyze individual defect images"
              icon={Image}
              onClick={() => setSelectedView('images')}
            />
            <ViewChoiceCard
              title="Analytics Dashboard"
              description="View defect statistics and trends"
              icon={LineChartIcon}
              onClick={() => setSelectedView('analytics')}
            />
          </div>
        </>
      );
    }

    if (selectedView === 'analytics') {
      return (
        <>
          <NavigationHeader 
            title="Defect Analytics"
            subtitle={`${selectedProduct} - ${selectedSize} size`}
            onBack={() => setSelectedView(null)}
          />
          <AnalyticsView 
            defectData={defectClasses}
            product={selectedProduct}
            size={selectedSize}
          />
        </>
      );
    }

    if (!selectedDefect) {
      const defects = defectClasses[selectedProduct]?.[selectedSize] || [];
      
      return (
        <>
          <NavigationHeader 
            title="Select Defect Type"
            subtitle={`${selectedProduct} - ${selectedSize} size`}
            onBack={() => setSelectedView(null)}
          />
          {defects.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {defects.map((defect, index) => (
                <DefectCard 
                  key={index}
                  defect={defect}
                  onSelect={fetchImages}
                />
              ))}
            </div>
          ) : (
            <NoDataAlert />
          )}
        </>
      );
    }

    return (
      <>
        <NavigationHeader 
          title={selectedDefect.defect}
          subtitle={`${selectedProduct} - ${selectedSize} size`}
          onBack={() => setSelectedDefect(null)}
        />
        {images.length > 0 ? renderImageGrid() : <NoImagesAlert />}
      </>
    );
  };

  return (
    <div className="min-h-screen bg-gray-900 p-4 sm:p-6">
      <div className="max-w-7xl mx-auto">
        <h1 className="text-3xl font-bold text-white mb-8 text-center bg-gradient-to-r from-blue-400 via-purple-400 to-pink-400 bg-clip-text text-transparent">
          Defect Analysis System
        </h1>
        {renderContent()}
        {selectedImage && (
          <ImageViewer 
            image={selectedImage}
            images={images}
            currentIndex={currentImageIndex}
            setCurrentIndex={setCurrentImageIndex}
            onClose={() => {
              setSelectedImage(null);
              setCurrentImageIndex(0);
            }}
          />
        )}
      </div>
    </div>
  );
};
export default DefectView;